import ArticleToolbar from "@features/toolbar/ArticleToolbar/ArticleToolbar";
import ArticleGooglePreferedSourceButton from "@features/toolbar/ArticleToolbar/components/ArticleGooglePreferedSourceButton";
import { PurchaseLinkProps } from "@features/toolbar/ArticleToolbar/utils/types";
import { useWindowSize } from "@hooks/useWindowSize";
import ArticleByline from "@routes/Article/components/ArticleByline/ArticleByline";
import ArticleKicker from "@routes/Article/components/ArticleKicker";
import ArticleSubscriberLabel from "@routes/Article/components/ArticleSubscriberLabel";
import { LambdaResponsePayload } from "@routes/Article/types/Alacrity";
import { FeedArticleData } from "@routes/Article/utils/types";
import useIsAppWebView from "@src/app/hooks/useIsAppWebView";
import { cn } from "@util/helpers";

type ArticleTopFoldProps = {
  /** Ref for a empty div starting point to enable the header*/
  displayArticleRef?: React.Ref<HTMLDivElement> | null;
  /** Indicator if summary is available */
  isSummaryAvailable: boolean | undefined;
  /** Flag to check if article is podcast article */
  isPodcastAudioArticle: boolean;
  /** Article data for the podcast article */
  article: FeedArticleData;
  /** Boolean indicating if the article is a premium article */
  isPremium: boolean;
  /** Function to handle gift url modal */
  handleGenerateGiftURLModal: () => void;
  /** Function to handle the gift modal button click */
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Flag to check if article is chinese article*/
  isChinese: boolean;
  alacrityRes?: LambdaResponsePayload;
  /** Purchase article details to determine if the purchase link should be displayed */
  purchaseArticleDetails?: PurchaseLinkProps;
  /** Flag to check if the article is author type */
  isAuthorOnly?: boolean;
};

/**
 * Component to display top section of article page
 */
const ArticleTopFold = ({
  displayArticleRef,
  isSummaryAvailable,
  isPodcastAudioArticle,
  article: {
    tags,
    id,
    title,
    kicker,
    standfirst,
    publishTime,
    urlPath,
    shortUrl,
    authors,
    hasUpdatedTimeDisplayed,
    editedTime,
  },
  purchaseArticleDetails,
  setIsModalOpen,
  isPremium,
  handleGenerateGiftURLModal,
  isChinese,
  alacrityRes,
  isAuthorOnly,
}: ArticleTopFoldProps): React.ReactElement => {
  const { isScreenMD } = useWindowSize();
  const isAppWebView = useIsAppWebView();

  if (isPodcastAudioArticle) return <div ref={displayArticleRef} />;

  return (
    <>
      {isPremium || kicker ? (
        <div className="flex flex-wrap items-center pb-2 md:px-8 md:pb-4">
          <ArticleKicker
            kicker={kicker}
            className="font-base font-light uppercase"
          />

          {isPremium && kicker ? (
            <div
              className="ml-3 mr-2 font-poppins text-2xl font-light leading-normal tracking-[0.075rem] text-gray-650"
              data-testid="kicker-subscriber-label-separator"
            >
              ·
            </div>
          ) : null}

          <ArticleSubscriberLabel
            isPremium={isPremium}
            displayDot={false}
            rootClassName={"font-base font-light"}
          />
        </div>
      ) : null}

      <h1
        data-testid="article-title"
        className={cn(
          "mb-2 font-lct text-4xl font-bold leading-normal -tracking-2% text-gray-850 md:px-8 md:text-10xl",
          { "font-notoSansSC tracking-normal": isChinese }
        )}
      >
        {title}
      </h1>

      {standfirst ? (
        <p
          className={cn(
            "mb-4 mt-2 font-lucida text-lg font-medium leading-[1.7] -tracking-5% text-gray-850",
            " md:px-8 md:text-xl",
            { "font-notoSansSC tracking-normal": isChinese }
          )}
        >
          {standfirst}
        </p>
      ) : null}

      {!isAppWebView ? (
        <div className="md:px-8">
          <ArticleToolbar
            shareUrl={shortUrl || ""}
            articleTitle={title}
            isPremium={isPremium}
            handleGenerateGiftURLModal={handleGenerateGiftURLModal}
            setIsModalOpen={setIsModalOpen}
            alacrityRes={alacrityRes}
            displayPurchase={purchaseArticleDetails}
            keyword={tags}
            cueId={id}
            isSummaryAvailable={isSummaryAvailable}
            isChinese={isChinese}
            articleUrl={urlPath}
          />

          {isScreenMD ? (
            <div className="mb-6 h-[1px] w-full bg-gray-175" />
          ) : null}
        </div>
      ) : null}

      <div ref={displayArticleRef} />

      {!isScreenMD ? (
        <div className="border-t border-gray-175">
          <ArticleGooglePreferedSourceButton
            className="my-4 px-4 py-3"
            articleUrl={urlPath}
          />
        </div>
      ) : null}

      {authors && isAuthorOnly ? (
        <div
          className={cn(
            "md:flex md:flex-wrap md:space-y-0",
            "relative mb-4  text-gray-850 md:mb-6 md:px-8",
            "md:before:left-8 md:before:right-8",
            { "min-h-[50px]": authors.length > 0 }
          )}
        >
          <ArticleByline
            authors={authors}
            editedTime={editedTime}
            updatedTime={publishTime}
            hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
            linkParams={new URLSearchParams({ ref: "article-top-byline" })}
          />
        </div>
      ) : null}
    </>
  );
};

export default ArticleTopFold;
